// src/encounter/services/use-l360-history-review-event.ts
// L360 I2: after the encounter saves the T02 "History & reconciliation" questionnaire, tell Workflows with ONE
// `history.review_submitted` event. FIRE-AND-FORGET, like advance-care-models-on-sign.ts: it runs only from the save's
// onSuccess, returns nothing, and `postL360Event` never throws and shows no error. Nothing in the save depends on it.
//   · only when env.l360EventsEnabled (dev deploy only), the template is T02, and the save returned an id;
//   · only when the reconciler section is `known` AND no section is `missing` (every one of the 13 is answered; a chip
//     counts). A draft posts nothing and leaves the last-posted revision as it was;
//   · the page autosaves every ~1.8 s, so it posts only when the revision (a hash of the 13 states) differs from the
//     last one posted for that QR id in this page session. The server is idempotent on the same key too.
import { useCallback, useRef } from 'react';
import { env } from '@/config/env';
import type { FhirQuestionnaire } from '@/types/template-studio-api';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { historyRevision, isHistoryReviewReady, isL360HistoryTemplate, summarizeHistorySections } from '../lib/l360-history-review';
import { answerSetRef } from '../lib/l360-care-model-inputs';
import { postL360Event } from './l360-api';

export interface HistoryReviewSave {
  questionnaire: FhirQuestionnaire | null | undefined;
  stored: QuestionnaireResponseResource | null | undefined;
  patientId: string | undefined;
  encounterId: string | undefined;
  reviewerId: string | undefined;
}

/** The last revision posted, per stored QR id. Exported so a test can hold its own. */
export type PostedRevisions = Map<string, string>;

export function postHistoryReviewEvent(posted: PostedRevisions, save: HistoryReviewSave): void {
  if (!env.l360EventsEnabled) return;
  const { questionnaire, stored, patientId, encounterId, reviewerId } = save;
  if (!stored?.id || !patientId || !encounterId || !questionnaire || !isL360HistoryTemplate(questionnaire)) return;
  const sections = summarizeHistorySections(questionnaire, stored);
  if (!isHistoryReviewReady(sections)) return;
  const revision = historyRevision(sections, reviewerId);
  // Keyed on the answer set id (else the QR id): after a sign, the next save is a new row in the same answer set.
  const ref = answerSetRef(stored) ?? stored.id;
  if (posted.get(ref) === revision) return;
  // Set BEFORE the request, so a second autosave cannot post the same revision while this one is in flight.
  posted.set(ref, revision);
  // postL360Event never rejects; the catch is a belt for a future change, so the page can never see an unhandled rejection.
  void Promise.resolve(
    postL360Event({
      eventType: 'history.review_submitted',
      patientId,
      sourceRef: ref,
      revision,
      payload: { encounterId, templateId: questionnaire.id ?? null, sections, reviewerId },
    }),
  )
    .then((result) => {
      // A failed post was not heard, so the next save may send it again. Accepted, no-enrolment and refused are final answers.
      // Only if the entry is still this revision: a newer one may have replaced it.
      if (result?.kind === 'failed' && posted.get(ref) === revision) posted.delete(ref);
    })
    .catch(() => undefined);
}

/** Returns the one call the page makes from the save's onSuccess. */
export function useL360HistoryReviewEvent(): (save: HistoryReviewSave) => void {
  const posted = useRef<PostedRevisions>(new Map());
  return useCallback((save) => postHistoryReviewEvent(posted.current, save), []);
}
